// src/web-app/lib/auth/session.js
// Web app session management - no local engine, direct Cloud Run API calls
import { AUTH_API_URL } from "../config.js";
import { getFirebaseIdToken, logoutFirebase } from "./firebase.js";
import { secureGet, secureSet, secureDelete } from "./secureStore.js";
import { request } from "../../../common/api.js";
import { showSessionExpiredBanner } from "../../../common/events.js";

const KEY_CLIENT_ID = "client_id";
const KEY_REFRESH   = "refresh_token";

let _accessToken = null;
let _accessExpEpoch = 0;
let _clientId = null;
let _refreshTimer = null;
let _ensuringSession = false; // Guard to prevent concurrent ensureSession calls
let _sessionExpiredHandler = null;
let _wakeHandlersAttached = false;

function nowEpoch() { return Math.floor(Date.now() / 1000); }

export function setSessionExpiredHandler(fn) {
  _sessionExpiredHandler = typeof fn === "function" ? fn : null;
}

function _attachWakeHandlers() {
  if (_wakeHandlersAttached || typeof window === "undefined") return;
  _wakeHandlersAttached = true;
  const wake = () => {
    refreshIfNeeded().catch((e) => console.warn("[AUTH] wake refresh failed", e));
  };
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") wake();
    });
    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
  } catch (e) {
    console.warn("[AUTH] failed to attach wake handlers", e);
  }
}

function scheduleRefresh(expiresInSec) {
  if (_refreshTimer) window.clearTimeout(_refreshTimer);
  const margin = Math.min(300, Math.max(60, Math.floor(expiresInSec / 2)));
  const delayMs = Math.max(5000, (expiresInSec - margin) * 1000);
  _refreshTimer = window.setTimeout(() => refreshIfNeeded().catch(console.error), delayMs);
}

export async function ensureSession(platform, appVersion) {
  // Prevent concurrent calls
  if (_ensuringSession) {
    console.warn(`[SESSION] ensureSession already in progress, waiting for completion...`);
    // Wait for the in-progress call to complete
    while (_ensuringSession) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    // Return the current session data
    return { clientId: _clientId, accessToken: _accessToken };
  }
  
  _ensuringSession = true;
  try {
    console.log(`[SESSION] ensureSession called at ${new Date().toISOString()}`);
    _clientId = await secureGet(KEY_CLIENT_ID);
    console.log(`[SESSION] Retrieved stored client_id: ${_clientId || 'none'}`);

    console.log(`[SESSION] Getting Firebase ID token...`);
    const fbIdToken = await getFirebaseIdToken(true);
    console.log(`[SESSION] Calling /session/bind at ${AUTH_API_URL}...`);
    
    const res = await request(`${AUTH_API_URL}/session/bind`, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({
        firebase_id_token: fbIdToken,
        client_id: _clientId || undefined,
        platform: platform || navigator.userAgent,
        app_version: appVersion || "web-app",
      }),
    });

    if (!res.ok) {
      const txt = await res.text();
      console.error(`[SESSION] /session/bind failed: ${res.status} ${txt}`);
      throw new Error(`/session/bind failed: ${res.status} ${txt}`);
    }
    const data = await res.json();

    _clientId = data.client_id;
    _accessToken = data.access_token;
    _accessExpEpoch = nowEpoch() + (data.expires_in ?? 3600);
    
    console.log(`[SESSION] Session bound successfully - client_id: ${_clientId}`);

    await secureSet(KEY_CLIENT_ID, _clientId);
    if (data.refresh_token) await secureSet(KEY_REFRESH, data.refresh_token);

    scheduleRefresh(data.expires_in ?? 3600);
    _attachWakeHandlers();

    // Web app: no local engine to spawn or push tokens to
    console.log(`[SESSION] ensureSession completed successfully (web app)`);

    return { clientId: _clientId, accessToken: _accessToken };
  } finally {
    _ensuringSession = false;
  }
}

export async function getAccessToken() {
  if (_accessToken && nowEpoch() < _accessExpEpoch - 30) return _accessToken;
  await refreshIfNeeded();
  if (!_accessToken) throw new Error("No access token available");
  return _accessToken;
}

export function getClientId() {
  if (!_clientId) throw new Error("No client_id in memory");
  return _clientId;
}

async function refreshIfNeeded() {
  const refresh = await secureGet(KEY_REFRESH);
  if (!refresh || !_clientId) return;
  if (_accessToken && nowEpoch() < _accessExpEpoch - 30) return;

  const res = await request(`${AUTH_API_URL}/session/refresh`, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify({
      client_id: _clientId,
      refresh_token: refresh,
    }),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    console.warn(`[AUTH] /session/refresh failed: ${res.status} ${errText}`);
    _accessToken = null;
    _accessExpEpoch = 0;
    if (res.status === 401 || res.status === 403) {
      try { await secureDelete(KEY_REFRESH); } catch {}
      try {
        if (_sessionExpiredHandler) {
          _sessionExpiredHandler({ reason: "refresh_revoked", status: res.status });
        } else {
          showSessionExpiredBanner({ context: "the Web App" });
        }
      } catch (e) { console.warn("[AUTH] session-expired handler threw", e); }
    } else {
      // Transient (network/5xx). Schedule a short retry.
      if (_refreshTimer) window.clearTimeout(_refreshTimer);
      _refreshTimer = window.setTimeout(() => {
        refreshIfNeeded().catch((e) => console.warn("[AUTH] retry refresh failed", e));
      }, 30000);
    }
    throw new Error(`session refresh failed: ${res.status}`);
  }
  const data = await res.json();
  _accessToken = data.access_token;
  _accessExpEpoch = nowEpoch() + (data.expires_in ?? 3600);
  if (data.refresh_token) await secureSet(KEY_REFRESH, data.refresh_token);

  scheduleRefresh(data.expires_in ?? 3600);
}

export async function logoutEverywhere() {
  try {
    const at = await getAccessToken();
    await request(`${AUTH_API_URL}/session/revoke`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${at}`
      },
      body: JSON.stringify({ client_id: getClientId() }),
    });
  } catch (e) {
    console.warn("revoke failed", e);
  }
  await secureDelete(KEY_REFRESH);
  await secureDelete(KEY_CLIENT_ID);
  _accessToken = null;
  _accessExpEpoch = 0;
  _clientId = null;
  if (_refreshTimer) { window.clearTimeout(_refreshTimer); _refreshTimer = null; }
  await logoutFirebase();
}

export function resetAuthState() {
  _accessToken = null;
  _accessExpEpoch = 0;
  _clientId = null;
  if (_refreshTimer) { 
    window.clearTimeout(_refreshTimer); 
    _refreshTimer = null; 
  }
}

